/**
 * Multi-Brand Logo Component
 * 
 * Supports both Purpose Driven and Our Square branding.
 * Automatically detects which brand to show based on context.
 * 
 * Purpose Driven:
 * - Circular design with three figures embracing
 * - Sun, hills, and road to horizon
 * - "Counseling & Peer Support"
 * 
 * Our Square:
 * - Heart/infinity symbol in blue-teal gradient
 * - "Love Heals" tagline
 * - Women's Transitional Housing
 * 
 * Usage:
 * - variant="full": Full logo with text (headers, login screens)
 * - variant="icon": Symbol only (app icons, navigation)
 * - brand="purpose-driven" | "our-square" | "auto" (default: auto)
 */

import React from 'react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';

// Purpose Driven Brand Colors
export const BRAND_COLORS = {
  primary: '#00A4B4',      // Primary Teal/Cyan Blue
  primaryDark: '#0068A6',  // Deep Blue (for "Purpose" text)
  secondary: '#80D153',    // Secondary Bright Green
  accent: '#FFDA6A',       // Accent Warm Yellow/Orange
  neutral: '#7F7F7F',      // Neutral Medium Gray
  white: '#FFFFFF',
  lightBg: '#FDF1D2',      // Light cream/peach background
  lightGreen: '#C4E3A9',   // Light green for hills
};

// Our Square Brand Colors
export const OUR_SQUARE_COLORS = {
  primary: '#0077B6',      // Deep Blue
  secondary: '#00B4D8',    // Bright Cyan
  accent: '#48CAE4',       // Light Teal
  dark: '#1A1A2E',         // Dark background
  neutral: '#7F7F7F',
  white: '#FFFFFF',
};

/**
 * Logo Component - Multi-brand support
 * @param {string} variant - "full" for logo with text, "icon" for symbol only
 * @param {string} size - "xs", "sm", "md", "lg", "xl", "2xl", "full"
 * @param {string} brand - "purpose-driven", "our-square", or "auto" (detects from user role)
 * @param {string} className - Additional CSS classes
 */
export function Logo({ variant = 'full', size = 'md', brand = 'auto', className }) {
  const { user } = useAuth();
  
  // Auto-detect brand based on user role or flags
  // Our Square roles: housing_resident, housing_coordinator, housing_staff
  // Also check is_housing_resident flag for clients
  const ourSquareRoles = ['housing_resident', 'housing_coordinator', 'housing_staff'];
  const isOurSquareUser = ourSquareRoles.includes(user?.role) || 
                          user?.is_housing_resident === true || 
                          user?.organization === 'our_square';
  const detectedBrand = brand === 'auto' 
    ? (isOurSquareUser ? 'our-square' : 'purpose-driven')
    : brand;

  const isOurSquare = detectedBrand === 'our-square';

  const sizeClasses = {
    xs: variant === 'full' ? 'w-[120px] h-auto' : 'w-8 h-8',
    sm: variant === 'full' ? 'w-[180px] h-auto' : 'w-10 h-10',
    md: variant === 'full' ? 'w-[260px] h-auto' : 'w-12 h-12',
    lg: variant === 'full' ? 'w-[340px] h-auto' : 'w-16 h-16',
    xl: variant === 'full' ? 'w-[420px] h-auto' : 'w-20 h-20',
    '2xl': variant === 'full' ? 'w-[520px] h-auto' : 'w-24 h-24',
    full: variant === 'full' ? 'w-full max-w-[600px] h-auto' : 'w-32 h-32',
  };

  // Select the right logo based on brand
  const src = isOurSquare
    ? '/images/our-square-logo.png'
    : (variant === 'full' ? '/images/purpose-driven-logo.png' : '/images/purpose-driven-icon.svg');

  const alt = isOurSquare
    ? 'Our Square - Love Heals'
    : (variant === 'full' ? 'Purpose Driven - Counseling & Peer Support' : 'Purpose Driven');

  return (
    <img
      src={src}
      alt={alt}
      className={cn(sizeClasses[size], 'object-contain', className)}
      data-testid={`logo-${variant}-${detectedBrand}`}
    />
  );
}

/**
 * LogoIcon - Shorthand for icon-only logo
 */
export function LogoIcon({ size = 'md', brand = 'auto', className }) {
  return <Logo variant="icon" size={size} brand={brand} className={className} />;
}

/**
 * LogoFull - Shorthand for full logo with text
 */
export function LogoFull({ size = 'md', brand = 'auto', className }) {
  return <Logo variant="full" size={size} brand={brand} className={className} />;
}

/**
 * Purpose Driven specific logo
 */
export function PurposeDrivenLogo({ variant = 'full', size = 'md', className }) {
  return <Logo variant={variant} size={size} brand="purpose-driven" className={className} />;
}

/**
 * Our Square specific logo
 */
export function OurSquareLogo({ variant = 'full', size = 'md', className }) {
  return <Logo variant={variant} size={size} brand="our-square" className={className} />;
}

/**
 * Brand Name Text Component
 * For cases where you need the text separately styled
 */
export function BrandName({ showTagline = true, brand = 'auto', className }) {
  const { user } = useAuth();
  
  // Auto-detect brand based on user role
  const ourSquareRoles = ['housing_resident', 'housing_coordinator', 'housing_staff'];
  const detectedBrand = brand === 'auto' 
    ? (ourSquareRoles.includes(user?.role) || user?.organization === 'our_square' ? 'our-square' : 'purpose-driven')
    : brand;

  const isOurSquare = detectedBrand === 'our-square';

  if (isOurSquare) {
    return (
      <div className={cn('flex flex-col', className)}>
        <span 
          className="font-bold text-lg" 
          style={{ 
            background: `linear-gradient(135deg, ${OUR_SQUARE_COLORS.primary}, ${OUR_SQUARE_COLORS.accent})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontFamily: "'Open Sans', Arial, sans-serif" 
          }}
        >
          Our Square
        </span>
        {showTagline && (
          <span 
            className="text-xs italic"
            style={{ color: OUR_SQUARE_COLORS.accent, fontFamily: "'Open Sans', Arial, sans-serif" }}
          >
            Love Heals
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="flex items-baseline gap-1">
        <span 
          className="font-bold" 
          style={{ color: BRAND_COLORS.primaryDark, fontFamily: "'Open Sans', 'Lato', Arial, sans-serif" }}
        >
          Purpose
        </span>
        <span 
          className="font-bold" 
          style={{ color: BRAND_COLORS.secondary, fontFamily: "'Open Sans', 'Lato', Arial, sans-serif" }}
        >
          Driven
        </span>
      </div>
      {showTagline && (
        <span 
          className="text-xs tracking-wider uppercase"
          style={{ color: BRAND_COLORS.neutral, fontFamily: "'Open Sans', 'Lato', Arial, sans-serif" }}
        >
          Counseling & Peer Support
        </span>
      )}
    </div>
  );
}

/**
 * Get brand colors based on context
 */
export function useBrandColors() {
  const { user } = useAuth();
  const isOurSquare = user?.role === 'housing_resident' || 
                      user?.role === 'housing_coordinator' || 
                      user?.role === 'housing_staff' ||
                      user?.is_housing_resident === true || 
                      user?.organization === 'our_square';
  return isOurSquare ? OUR_SQUARE_COLORS : BRAND_COLORS;
}

export default Logo;
